import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = 'https://oauth.remote-assistant.ai';

function StatCard({ label, value, sub, change }) {
  const arrow = change > 0 ? '↑' : change < 0 ? '↓' : '';
  const color = change > 0 ? '#10b981' : change < 0 ? '#ef4444' : '#979ba4';
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
      <p className="text-sm font-inter" style={{ color: '#979ba4' }}>{label}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-montserrat font-semibold mt-1" style={{ color: '#222324' }}>{value}</p>
        {change !== null && change !== undefined && (
          <p className="text-sm font-inter font-medium" style={{ color }}>
            {arrow} {Math.abs(change)}%
          </p>
        )}
      </div>
      {sub && <p className="text-xs font-inter mt-1" style={{ color: '#979ba4' }}>{sub}</p>}
    </div>
  );
}

function VitalBadge({ label, value, goodThreshold, unit }) {
  if (value == null) return <span style={{ color: '#979ba4' }}>—</span>;
  const pass = value <= goodThreshold;
  return (
    <span>
      <span style={{ color: '#222324' }}>{value}{unit}</span>{' '}
      <span style={{ fontSize: 11, fontWeight: 600, color: pass ? '#16a34a' : '#dc2626' }}>
        {pass ? '✓' : '✗'}
      </span>
    </span>
  );
}

export default function SiteAnalytics() {
  const { getAuthToken, signOut } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const token = await getAuthToken();
        const res = await fetch(`${API_BASE}/admin/analytics/summary?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [days, getAuthToken]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <img src="/assets/logo---dark-with-color.png" alt="Remote Assistant" className="h-8 w-auto" />
              <Link to="/dashboard" className="text-sm font-inter" style={{ color: '#979ba4' }}>← Dashboard</Link>
            </div>
            <div className="flex items-center">
              <button onClick={signOut} className="font-inter text-sm font-medium px-3 py-2 rounded-md" style={{ color: '#979ba4' }}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-montserrat font-semibold" style={{ color: '#222324' }}>Site Analytics</h2>
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-sm font-inter border rounded-lg px-3 py-2"
            style={{ borderColor: '#e5e7eb', color: '#222324' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 text-sm">{error}</div>}

        {loading ? (
          <div className="text-center py-20 font-inter" style={{ color: '#979ba4' }}>Loading...</div>
        ) : data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard label="Total Page Views" value={data.total_views.toLocaleString()} change={data.views_change} />
              <StatCard label="Total Conversions" value={data.total_conversions.toLocaleString()} change={data.conversions_change} />
              <StatCard label="Conversion Rate" value={`${data.conversion_rate}%`} change={data.rate_change} sub={`over last ${days} days`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>Top Pages</h3>
                {data.top_pages.length === 0 ? (
                  <p className="text-sm font-inter" style={{ color: '#979ba4' }}>No data yet</p>
                ) : (
                  <table className="w-full text-sm font-inter">
                    <thead>
                      <tr style={{ color: '#979ba4' }}>
                        <th className="text-left pb-2">Path</th>
                        <th className="text-right pb-2">Views</th>
                        <th className="text-right pb-2">Conversions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_pages.map(p => (
                        <tr key={p.path} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td className="py-2" style={{ color: '#222324' }}>{p.path}</td>
                          <td className="py-2 text-right" style={{ color: '#222324' }}>{p.views}</td>
                          <td className="py-2 text-right" style={{ color: '#055efd' }}>{p.conversions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>Daily Trend</h3>
                {data.daily_trend.length === 0 ? (
                  <p className="text-sm font-inter" style={{ color: '#979ba4' }}>No data yet</p>
                ) : (
                  <table className="w-full text-sm font-inter">
                    <thead>
                      <tr style={{ color: '#979ba4' }}>
                        <th className="text-left pb-2">Date</th>
                        <th className="text-right pb-2">Views</th>
                        <th className="text-right pb-2">Organic</th>
                        <th className="text-right pb-2">Conversions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.daily_trend].reverse().map(d => (
                        <tr key={d.date} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td className="py-2" style={{ color: '#222324' }}>{d.date}</td>
                          <td className="py-2 text-right" style={{ color: '#222324' }}>{d.views}</td>
                          <td className="py-2 text-right" style={{ color: '#16a34a' }}>{d.organic_views || 0}</td>
                          <td className="py-2 text-right" style={{ color: '#055efd' }}>{d.conversions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>Traffic Sources</h3>
                {!data.traffic_sources?.length ? (
                  <p className="text-sm font-inter" style={{ color: '#979ba4' }}>No data yet</p>
                ) : (() => {
                  const convMap = {};
                  (data.conversion_sources || []).forEach(c => { convMap[c.source] = c.conversions; });
                  return (
                    <table className="w-full text-sm font-inter">
                      <thead><tr style={{ color: '#979ba4' }}>
                        <th className="text-left pb-2">Source</th>
                        <th className="text-right pb-2">Views</th>
                        <th className="text-right pb-2">Conv.</th>
                        <th className="text-right pb-2">Rate</th>
                      </tr></thead>
                      <tbody>
                        {data.traffic_sources.map(s => {
                          const convs = convMap[s.source] || 0;
                          const rate = s.views > 0 ? ((convs / s.views) * 100).toFixed(1) : '0.0';
                          return (
                            <tr key={s.source} style={{ borderTop: '1px solid #f3f4f6' }}>
                              <td className="py-2" style={{ color: '#222324' }}>{s.source}</td>
                              <td className="py-2 text-right" style={{ color: '#222324' }}>{s.views}</td>
                              <td className="py-2 text-right" style={{ color: '#055efd' }}>{convs}</td>
                              <td className="py-2 text-right" style={{ color: Number(rate) > 0 ? '#16a34a' : '#979ba4' }}>{rate}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>Top Referrers</h3>
                {!data.top_referrers?.length ? (
                  <p className="text-sm font-inter" style={{ color: '#979ba4' }}>No data yet</p>
                ) : (
                  <table className="w-full text-sm font-inter">
                    <thead><tr style={{ color: '#979ba4' }}><th className="text-left pb-2">Referrer</th><th className="text-right pb-2">Views</th></tr></thead>
                    <tbody>
                      {data.top_referrers.map(r => (
                        <tr key={r.referrer} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td className="py-2" style={{ color: '#222324', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.referrer}</td>
                          <td className="py-2 text-right" style={{ color: '#222324' }}>{r.views}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>Core Web Vitals <span className="text-xs font-normal" style={{ color: '#979ba4' }}>p75</span></h3>
                {!data.web_vitals || data.web_vitals.sample_count === 0 ? (
                  <p className="text-sm font-inter" style={{ color: '#979ba4' }}>No data yet</p>
                ) : (
                  <table className="w-full text-sm font-inter">
                    <thead><tr style={{ color: '#979ba4' }}><th className="text-left pb-2">Metric</th><th className="text-right pb-2">p75</th></tr></thead>
                    <tbody>
                      <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td className="py-2" style={{ color: '#222324' }}>LCP</td>
                        <td className="py-2 text-right"><VitalBadge value={data.web_vitals.lcp_p75} goodThreshold={2.5} unit="s" /></td>
                      </tr>
                      <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td className="py-2" style={{ color: '#222324' }}>CLS</td>
                        <td className="py-2 text-right"><VitalBadge value={data.web_vitals.cls_p75} goodThreshold={0.1} unit="" /></td>
                      </tr>
                      <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td className="py-2" style={{ color: '#222324' }}>INP</td>
                        <td className="py-2 text-right"><VitalBadge value={data.web_vitals.inp_p75} goodThreshold={200} unit="ms" /></td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <p className="text-xs mt-3" style={{ color: '#979ba4' }}>{data.web_vitals?.sample_count || 0} samples</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
